import { Link, X } from "lucide-react";
import { ListItemType, OptimisticListItem } from "modules/lists/types/lists";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Item, ItemActions, ItemContent, ItemDescription, ItemMedia } from "@/components/ui/item";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";

interface OptimisticItemRowProps {
    item: OptimisticListItem;
    /** Dismisses the row. Only offered when the save failed. */
    onRemove: () => void;
}

/**
 * Renders a list item placeholder while its backend save is in flight (edit mode).
 * - pending: shows the product name with a spinner and "Guardando…" hint.
 * - error: shows a destructive hint and a dismiss action.
 */
const OptimisticItemRow: React.FC<OptimisticItemRowProps> = ({ item, onRemove }) => {
    const isPending = item.status === "pending";

    return (
        <Item
            variant="default"
            size="sm"
            data-testid="optimistic-item"
            className={cn(
                "w-full max-w-full min-w-0 overflow-hidden flex-nowrap",
                isPending && "opacity-70",
                !isPending && "border-destructive/40 bg-destructive/5"
            )}
        >
            <ItemMedia className="shrink-0">
                {isPending ? (
                    <Spinner className="h-4 w-4 text-muted-foreground" />
                ) : (
                    <X className="h-4 w-4 text-destructive" />
                )}
            </ItemMedia>

            <ItemContent className="flex-1 basis-0 min-w-0 overflow-hidden">
                <span className="block w-full max-w-full min-w-0 truncate text-sm leading-snug font-medium">
                    {item.name ?? item.rawDescription}
                </span>
                <ItemDescription className="min-w-0 max-w-full overflow-hidden">
                    {item.type === ListItemType.LINKED ? (
                        <Badge variant="outline" className="text-muted-foreground">
                            <Link className="h-3.5 w-3.5 shrink-0 best-price" />
                            Producto específico
                        </Badge>
                    ) : null}
                    {isPending ? (
                        <span className="text-xs text-muted-foreground ml-1">Guardando…</span>
                    ) : (
                        <span className="text-xs text-destructive ml-1 block">No se pudo guardar</span>
                    )}
                </ItemDescription>
            </ItemContent>

            {!isPending && (
                <ItemActions className="shrink-0 ml-auto">
                    <Button variant="ghost" size="icon" onClick={onRemove} aria-label="Descartar">
                        <X className="h-4 w-4" />
                    </Button>
                </ItemActions>
            )}
        </Item>
    );
};

export { OptimisticItemRow };
